import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ROLES, roleDef, type RoleKey } from '@piaip/shared';
import { sx, sxc } from '../sx';
import { useApp } from '../state';
import { api, type AdminUser, type AuditEntry } from '../api';
import {
  BTN_3D,
  BTN_PRIMARY,
  Field,
  INPUT,
  LABEL,
  SELECT,
} from '../components/ui';

const PANEL = 'background:#fff;border:1px solid #dbe3e0;border-radius:8px;padding:20px';
const PANEL_H = "font-family:'IBM Plex Serif',serif;font-size:18px;font-weight:600;margin-bottom:6px";
const PANEL_NOTE = 'font-size:13px;color:#5a6b68;line-height:1.6;margin-bottom:18px';

export function DataMethod() {
  const { snapshot, can, mutate, notify, engine } = useApp();
  const canUsers = can('users');
  const importRef = useRef<HTMLInputElement>(null);

  const summary = useMemo(() => {
    const weeks = engine.weeks().length;
    const approved = engine.appr().length;
    return `${weeks} weeks recorded (${approved} approved) · ${(snapshot.issues ?? []).length} issues · ${
      (snapshot.packages ?? []).length
    } packages · ${(snapshot.payments ?? []).length} contractor payments · ${
      (snapshot.users ?? []).length
    } accounts.`;
  }, [engine, snapshot]);

  return (
    <div
      style={sx(
        'max-width:760px;margin:0 auto;padding:24px 16px 60px;display:flex;flex-direction:column;gap:16px',
      )}
    >
      {/* --------------------------------------------------------- data file */}
      <div style={sx(PANEL)}>
        <div style={sx(PANEL_H)}>Data file</div>
        <div style={sx(PANEL_NOTE)}>
          Every entry is held in the scheme database on the server, so the field office and the PMU
          see the same figures. Take a backup at the end of each week and keep it in the project
          folder — that one file carries the whole scheme, photographs included, to another machine.
        </div>
        <div style={sx('display:flex;gap:10px;flex-wrap:wrap;align-items:center')}>
          <a
            href={api.backupUrl}
            download
            style={sx(
              'background:#0f5c52;color:#fff;border:0;padding:12px 18px;border-radius:4px;font-size:14px;font-weight:600;cursor:pointer;text-decoration:none;display:inline-block',
            )}
          >
            Export backup file
          </a>
          {canUsers ? (
            <>
              <button
                type="button"
                onClick={() => importRef.current?.click()}
                style={sx(
                  'background:#eef4f2;color:#0f5c52;border:1px solid #cfe0dc;padding:12px 18px;border-radius:4px;font-size:14px;font-weight:600;cursor:pointer',
                )}
              >
                Import backup file
              </button>
              <input
                ref={importRef}
                type="file"
                accept="application/json"
                style={{ display: 'none' }}
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  e.target.value = '';
                  if (!file) return;
                  if (
                    !window.confirm(
                      'Importing replaces every weekly return, issue, package and payment now in the system. Continue?',
                    )
                  )
                    return;
                  const ok = await mutate(() => api.importBackup(file, false));
                  if (ok) notify('Backup restored.');
                }}
              />
              <button
                type="button"
                onClick={async () => {
                  if (!window.confirm('Delete all entered progress data? Accounts are kept.')) return;
                  const ok = await mutate(() => api.clearData());
                  if (ok) notify('All progress data cleared.');
                }}
                style={sx(
                  'background:none;border:1px solid #e3c9c5;color:#a3372c;padding:12px 16px;border-radius:4px;font-size:14px;cursor:pointer',
                )}
              >
                Clear all data
              </button>
            </>
          ) : null}
        </div>
        <div style={sx('margin-top:16px;font-size:12.5px;color:#5a6b68')}>{summary}</div>
      </div>

      {canUsers ? <Accounts /> : null}

      <MySignInPanel />

      {canUsers ? <AuditLog /> : null}

      {/* --------------------------------------------------------- method */}
      <div style={sx(PANEL)}>
        <div style={sx(PANEL_H)}>How progress is calculated</div>
        <div style={sx('font-size:13px;color:#33403d;line-height:1.65')}>
          <p style={sx('margin:0 0 10px')}>
            Only weeks approved by the PMU In-charge count towards progress. A week entered by a
            field user stays out of the dashboard and out of every report until it is approved.
            Payments recorded on the Contractor Payments tab count from the day they are entered,
            since they are drawn from the treasury record and need no site verification.
          </p>
          <p style={sx('margin:0 0 10px')}>
            Physical progress follows the approved tracker. Civil work carries 51.6%, electrical
            25.5% and mechanical 22.9% of the whole. Within civil work, the five components — new
            drains, rehabilitation of existing drains, operator quarters, pump houses and pumping
            stations — carry equal weight.
          </p>
          <p style={sx('margin:0 0 10px')}>
            Drains are measured by length completed against total length. Structures are measured by
            construction stage, on the standard convention: excavation 10%, plinth 20%,
            superstructure 50%, roof 70%, finishing 95%, handover 100%. Electrical and mechanical
            units follow their own stage weightings.
          </p>
          <p style={sx('margin:0')}>
            Land acquisition, environmental monitoring, contingencies and sales tax are financial and
            administrative milestones. They are reported separately and are not counted as physical
            construction progress.
          </p>
          <p style={sx('margin:10px 0 0')}>
            For the same reason, financial progress is measured like for like with physical
            progress: payments on civil, electrical and mechanical work against the construction
            budget of Rs 2,270.1 million, not everything paid against the full sanctioned cost of
            Rs 2,859.4 million. Money paid on land, environment, contingencies and tax is still
            shown — in the budget utilisation panel and the Financial Statement — but it does not
            inflate the financial progress figure.
          </p>
        </div>
      </div>
    </div>
  );
}

/** The signed-in user's own name and password, shared with the executive's account view. */
export function MySignInPanel() {
  const { me, refresh, setMe } = useApp();
  return (
    <div style={sx(PANEL)}>
      <div style={sx(PANEL_H)}>My sign-in</div>
      <div style={sx(PANEL_NOTE)}>
        You are signed in as <strong style={sx('color:#0d3b35')}>{me?.id}</strong> —{' '}
        {roleDef(me?.role).label}. {roleDef(me?.role).desc} Only the System Administrator can
        create accounts or change roles.
      </div>
      <SelfName
        initial={me?.name ?? ''}
        onSaved={(user) => {
          setMe(user);
          void refresh();
        }}
      />
      <ChangePassword onSaved={(user) => setMe(user)} />
    </div>
  );
}

function SelfName({ initial, onSaved }: { initial: string; onSaved: (user: any) => void }) {
  const { mutate, notify } = useApp();
  const [name, setName] = useState(initial);
  useEffect(() => setName(initial), [initial]);

  return (
    <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(190px,1fr));gap:12px')}>
      <Field label="Name shown on screen" htmlFor="self-name">
        <input
          id="self-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={async () => {
            if (!name.trim() || name === initial) {
              setName(initial);
              return;
            }
            const res = await mutate(() => api.renameSelf(name.trim()), { refresh: false });
            if (res) {
              onSaved(res.user);
              notify('Your display name has been updated.');
            }
          }}
          style={sx(
            'padding:10px;border:1px solid #d3dcda;border-radius:6px;font-size:14px;width:100%;box-sizing:border-box',
          )}
        />
      </Field>
    </div>
  );
}

function ChangePassword({ onSaved }: { onSaved: (user: any) => void }) {
  const { mutate, notify } = useApp();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');

  const submit = useCallback(async () => {
    setError('');
    if (next.length < 8) {
      setError('Choose a password of at least eight characters.');
      return;
    }
    if (next !== confirm) {
      setError('The two new passwords do not match.');
      return;
    }
    const res = await mutate(() => api.changePassword(current, next), { refresh: false });
    if (res) {
      onSaved(res.user);
      setCurrent('');
      setNext('');
      setConfirm('');
      notify('Your password has been changed.');
    }
  }, [confirm, current, mutate, next, notify, onSaved]);

  return (
    <div style={sx('margin-top:18px;border-top:1px solid #eef2f1;padding-top:16px')}>
      <div style={sxc(LABEL, 'margin-bottom:12px')}>Change my password</div>
      <div
        style={sx(
          'display:grid;grid-template-columns:repeat(auto-fit,minmax(170px,1fr));gap:12px;align-items:end',
        )}
      >
        <Field label="Current password" htmlFor="pw-cur">
          <input
            id="pw-cur"
            type="password"
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            style={sx(INPUT)}
          />
        </Field>
        <Field label="New password" htmlFor="pw-new">
          <input
            id="pw-new"
            type="password"
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            style={sx(INPUT)}
          />
        </Field>
        <Field label="Repeat new password" htmlFor="pw-rep">
          <input
            id="pw-rep"
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            style={sx(INPUT)}
          />
        </Field>
        <button type="button" onClick={() => void submit()} style={sx(BTN_3D)}>
          Change password
        </button>
      </div>
      {error ? (
        <div
          role="alert"
          style={sx('margin-top:12px;font-size:13px;color:#a3372c;background:#fbeae7;border-radius:5px;padding:9px 11px')}
        >
          {error}
        </div>
      ) : null}
    </div>
  );
}

/* ----------------------------------------------------------- accounts */

function Accounts() {
  const { me, mutate, refresh } = useApp();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [msg, setMsg] = useState('');
  const [nu, setNu] = useState<{ id: string; name: string; pass: string; role: RoleKey }>({
    id: '',
    name: '',
    pass: '',
    role: 'field',
  });

  const load = useCallback(async () => {
    try {
      const { users } = await api.users();
      setUsers(users);
    } catch {
      /* the panel is only rendered for an administrator */
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  const addUser = async () => {
    setMsg('');
    const id = nu.id.trim().toLowerCase();
    if (!id) {
      setMsg('A user ID is required.');
      return;
    }
    if (nu.pass.length < 8) {
      setMsg('Set a password of at least eight characters.');
      return;
    }
    const res = await mutate(
      () => api.createUser({ id, name: nu.name.trim() || id, password: nu.pass, role: nu.role }),
      { refresh: false },
    );
    if (res) {
      setNu({ id: '', name: '', pass: '', role: 'field' });
      setMsg(`Account “${id}” created. Give the holder the password you set; they will be asked to change it.`);
      await load();
      await refresh();
    }
  };

  return (
    <div style={sx(PANEL)}>
      <div style={sx(PANEL_H)}>Accounts and roles</div>
      <div style={sx('font-size:13px;color:#5a6b68;line-height:1.6;margin-bottom:16px')}>
        Add or remove accounts, set each one&rsquo;s role, and reset any password. No other role can
        see or change accounts. Passwords are stored hashed and cannot be read back — reset one to
        issue a new password.
      </div>

      <div style={sx('display:flex;flex-direction:column;gap:14px')}>
        {users.map((u) => (
          <AccountCard
            key={u.id}
            user={u}
            isSelf={u.id === me?.id}
            onChanged={async () => {
              await load();
              await refresh();
            }}
          />
        ))}
      </div>

      <div style={sx('margin-top:18px;border-top:1px solid #eef2f1;padding-top:16px')}>
        <div style={sx('font-size:14px;font-weight:600;margin-bottom:12px')}>Add an account</div>
        <div
          style={sx(
            'display:grid;grid-template-columns:repeat(auto-fit,minmax(150px,1fr));gap:12px;align-items:end',
          )}
        >
          <Field label="User ID" htmlFor="nu-id">
            <input
              id="nu-id"
              type="text"
              autoCapitalize="none"
              value={nu.id}
              onChange={(e) => setNu((s) => ({ ...s, id: e.target.value }))}
              style={sxc(INPUT, "font-family:'IBM Plex Mono',monospace")}
            />
          </Field>
          <Field label="Full name" htmlFor="nu-name">
            <input
              id="nu-name"
              type="text"
              value={nu.name}
              onChange={(e) => setNu((s) => ({ ...s, name: e.target.value }))}
              style={sx(INPUT)}
            />
          </Field>
          <Field label="Role" htmlFor="nu-role">
            <select
              id="nu-role"
              value={nu.role}
              onChange={(e) => setNu((s) => ({ ...s, role: e.target.value as RoleKey }))}
              style={sx(SELECT)}
            >
              {ROLES.map((r) => (
                <option key={r.k} value={r.k}>
                  {r.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Password" htmlFor="nu-pass">
            <input
              id="nu-pass"
              type="text"
              autoComplete="new-password"
              value={nu.pass}
              onChange={(e) => setNu((s) => ({ ...s, pass: e.target.value }))}
              style={sxc(INPUT, "font-family:'IBM Plex Mono',monospace")}
            />
          </Field>
          <button type="button" onClick={() => void addUser()} style={sx(BTN_3D)}>
            Create account
          </button>
        </div>
        {msg ? (
          <div
            style={sx(
              'margin-top:12px;font-size:13px;color:#0f5c52;background:#eef4f2;border-radius:5px;padding:9px 11px',
            )}
          >
            {msg}
          </div>
        ) : null}
      </div>

      <div style={sx('margin-top:18px;border-top:1px solid #eef2f1;padding-top:16px')}>
        <div style={sxc(LABEL, 'margin-bottom:10px')}>What each role may do</div>
        <div style={sx('display:flex;flex-direction:column;gap:8px')}>
          {ROLES.map((r) => (
            <div key={r.k} style={sx('font-size:12.5px;color:#33403d;line-height:1.5')}>
              <strong>{r.label}</strong> — {r.desc}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function AccountCard({
  user,
  isSelf,
  onChanged,
}: {
  user: AdminUser;
  isSelf: boolean;
  onChanged: () => void | Promise<void>;
}) {
  const { mutate, notify } = useApp();
  const [name, setName] = useState(user.name);
  const [reset, setReset] = useState('');
  useEffect(() => setName(user.name), [user.name]);

  return (
    <div
      style={sx(
        'border:1px solid #e4eae8;border-radius:8px;padding:14px;background:linear-gradient(180deg,#fdfefe,#f6faf9);box-shadow:0 1px 0 #fff inset,0 8px 16px -14px rgba(13,59,53,.5)',
      )}
    >
      <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(170px,1fr));gap:12px')}>
        <Field label="User ID" htmlFor={`u-id-${user.id}`}>
          <input
            id={`u-id-${user.id}`}
            type="text"
            value={user.id}
            disabled
            style={sxc(INPUT, "font-family:'IBM Plex Mono',monospace")}
          />
        </Field>
        <Field label="Name shown on screen" htmlFor={`u-name-${user.id}`}>
          <input
            id={`u-name-${user.id}`}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={async () => {
              if (!name.trim() || name === user.name) {
                setName(user.name);
                return;
              }
              const ok = await mutate(() => api.patchUser(user.id, { name: name.trim() }), {
                refresh: false,
              });
              if (ok) await onChanged();
            }}
            style={sx(INPUT)}
          />
        </Field>
        <Field label="Role" htmlFor={`u-role-${user.id}`}>
          <select
            id={`u-role-${user.id}`}
            value={user.role}
            onChange={async (e) => {
              const ok = await mutate(
                () => api.patchUser(user.id, { role: e.target.value as RoleKey }),
                { refresh: false },
              );
              if (ok) await onChanged();
            }}
            style={sx(SELECT)}
          >
            {ROLES.map((r) => (
              <option key={r.k} value={r.k}>
                {r.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Set a new password" htmlFor={`u-pass-${user.id}`}>
          <div style={sx('display:flex;gap:6px')}>
            <input
              id={`u-pass-${user.id}`}
              type="text"
              autoComplete="new-password"
              value={reset}
              placeholder="not shown"
              onChange={(e) => setReset(e.target.value)}
              style={sxc(INPUT, "font-family:'IBM Plex Mono',monospace")}
            />
            <button
              type="button"
              disabled={reset.length < 8}
              onClick={async () => {
                const ok = await mutate(() => api.patchUser(user.id, { password: reset }), {
                  refresh: false,
                });
                if (ok) {
                  setReset('');
                  notify(`Password reset for “${user.id}”.`);
                  await onChanged();
                }
              }}
              style={sxc(BTN_PRIMARY, 'padding:9px 12px;font-size:13px;white-space:nowrap')}
            >
              Set
            </button>
          </div>
        </Field>
      </div>
      <div
        style={sx(
          'display:flex;justify-content:space-between;align-items:center;gap:12px;flex-wrap:wrap;margin-top:10px',
        )}
      >
        <div style={sx('font-size:12px;color:#5a6b68;line-height:1.5;max-width:640px')}>
          {user.roleDesc}{' '}
          {isSelf ? <strong style={sx('color:#0f5c52')}>This is you</strong> : null}
          {user.mustChangePassword ? (
            <em style={sx('color:#b06a12')}> — still on the password issued to them</em>
          ) : null}
        </div>
        {!isSelf ? (
          <button
            type="button"
            onClick={async () => {
              if (!window.confirm(`Remove the account “${user.id}”?`)) return;
              const ok = await mutate(() => api.deleteUser(user.id), { refresh: false });
              if (ok) await onChanged();
            }}
            style={sx(
              'background:none;border:1px solid #e3c9c5;color:#a3372c;padding:8px 13px;border-radius:5px;font-size:12.5px;cursor:pointer',
            )}
          >
            Remove account
          </button>
        ) : null}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------- audit */

function AuditLog() {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    api
      .audit()
      .then((r) => setEntries(r.entries))
      .catch(() => setEntries([]));
  }, [open]);

  return (
    <div style={sx(PANEL)}>
      <div style={sx('display:flex;justify-content:space-between;align-items:center;gap:12px;flex-wrap:wrap')}>
        <div>
          <div style={sxc(PANEL_H, 'margin-bottom:2px')}>Audit trail</div>
          <div style={sx('font-size:13px;color:#5a6b68')}>
            Who signed in, who submitted a week and who approved it.
          </div>
        </div>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          style={sx(
            'background:#eef4f2;color:#0f5c52;border:1px solid #cfe0dc;padding:9px 14px;border-radius:4px;font-size:13px;font-weight:600;cursor:pointer',
          )}
        >
          {open ? 'Hide' : 'Show last 300 actions'}
        </button>
      </div>
      {open ? (
        <div style={sx('margin-top:16px;max-height:340px;overflow:auto;border:1px solid #eef2f1;border-radius:6px')}>
          <table style={sx('width:100%;border-collapse:collapse')}>
            <tbody>
              {entries.map((e, i) => (
                <tr key={i}>
                  <td
                    style={sx(
                      "padding:6px 10px;border-bottom:1px solid #f2f6f5;font-size:11.5px;color:#5a6b68;white-space:nowrap;font-family:'IBM Plex Mono',monospace",
                    )}
                  >
                    {e.at.replace('T', ' ').slice(0, 16)}
                  </td>
                  <td style={sx('padding:6px 10px;border-bottom:1px solid #f2f6f5;font-size:12.5px')}>
                    {e.user_name || e.user_id || '—'}
                  </td>
                  <td
                    style={sx(
                      "padding:6px 10px;border-bottom:1px solid #f2f6f5;font-size:12px;color:#0f5c52;font-family:'IBM Plex Mono',monospace",
                    )}
                  >
                    {e.action}
                  </td>
                  <td style={sx('padding:6px 10px;border-bottom:1px solid #f2f6f5;font-size:12.5px;color:#5a6b68')}>
                    {e.detail}
                  </td>
                </tr>
              ))}
              {entries.length === 0 ? (
                <tr>
                  <td style={sx('padding:14px;font-size:13px;color:#5a6b68')}>Nothing recorded yet.</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
